import { Link } from '@tanstack/react-router';
import { Card, Spinner } from '@tatra/ui';
import type { ReactNode } from 'react';
import { useSession } from './lib/session.js';

export function Brand({ size = 'md', onDark = false }: { size?: 'md' | 'lg'; onDark?: boolean }) {
  const { config } = useSession();
  const name = config?.brandName || 'Partner portal';
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      {config?.hasLogo && (
        <img
          src="/api/v1/partner/config/logo"
          alt=""
          className={size === 'lg' ? 'h-12 w-auto max-w-40' : 'h-8 w-auto max-w-28'}
        />
      )}
      {!config?.hasLogo && (
        <span
          aria-hidden="true"
          className="h-2.5 w-2.5 shrink-0 rounded-full bg-[linear-gradient(135deg,#ff6fc9,#ec1f9c_45%,#a855f7)]"
        />
      )}
      <span
        className={`truncate font-display font-semibold tracking-[-0.01em] ${onDark ? 'text-nav-text' : 'text-text'} ${size === 'lg' ? 'text-xl' : 'text-base'}`}
      >
        {name}
      </span>
    </span>
  );
}

/** Frame for the signed-out screens: brand, one card, a footer link. */
export function AuthFrame({
  title,
  intro,
  children,
  footer,
}: {
  title: string;
  intro?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { config, configError } = useSession();
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-5 px-4 py-8">
      <Link to="/sign-in" className="self-center">
        <Brand size="lg" />
      </Link>
      <Card className="space-y-4 p-5 sm:p-6">
        <div>
          <h1 className="font-display text-xl font-semibold tracking-[-0.01em] text-text">{title}</h1>
          {intro && <p className="mt-1 text-sm text-text-muted">{intro}</p>}
        </div>
        {configError && !config ? (
          <p role="alert" className="text-sm text-danger">
            This portal is not available on this address.
          </p>
        ) : (
          children
        )}
      </Card>
      {footer && <p className="text-center text-sm text-text-muted">{footer}</p>}
    </main>
  );
}

export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-md border border-danger/40 bg-danger-bg px-3 py-2 text-sm text-danger">
      {message}
    </p>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="py-8 text-center">
      <Spinner label={label} />
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-bg-elevated p-3">
      <p className="text-xs text-text-muted">{label}</p>
      <p className="mt-0.5 truncate text-lg font-semibold text-text">{value}</p>
      {hint && <p className="text-xs text-text-faint">{hint}</p>}
    </div>
  );
}
